import UserAvatar from '@/components/ui/userAvatar';
import { AuthContext, ConferenceContext } from '@/store';
import { useNodeProps } from '@/node-editor/hooks';
import { INode, INodeProps } from '@/types/node';
import { Box, ButtonBase, Icon } from '@mui/material';
import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { IContent } from './contract';
import moment from 'moment';
import { useTheme, Theme } from '@mui/material/styles';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

interface IContainerStyleProps {
  override: Record<string, any>;
}

const useContainerStyles = (
  theme: Theme,
  { override }: IContainerStyleProps
) => ({
  root: {
    width: '100% !important',
    padding: `${theme.spacing(1)} !important`,
    ...override.root,
  },
});

const useItemStyles = (theme: Theme) => ({
  root: {
    display: 'flex !important',
    alignItems: 'flex-start !important',
    textAlign: 'left !important',
    paddingBottom: `${theme.spacing(1)} !important`,
    borderBottom: `1px solid ${theme.palette.background.shadedown_10} !important`,
    marginBottom: `${theme.spacing(1)} !important`,
    '&:last-child': {
      borderBottom: 'none !important',
      marginBottom: '0px !important',
    },
  },
  icon: {
    fontSize: '1.2rem',
    marginTop: '3px !important',
    marginRight: `${theme.spacing(1)} !important`,
  },
  user: {
    fontSize: '0.8rem !important',
    display: 'flex !important',
    alignItems: 'center !important',
  },
  content: {
    flexGrow: '1 !important',
  },
  replyTo: {
    fontSize: '0.7rem !important',
    fontWeight: 'bold !important',
    opacity: '0.5 !important',
    '&:first-letter': {
      textTransform: 'uppercase !important',
    },
  },
  text: {
    fontSize: '1.2rem !important',
    marginBottom: `${theme.spacing(0.5)} !important`,
    '&:first-letter': {
      textTransform: 'uppercase !important',
    },
  },
  date: {
    flexGrow: '1 !important',
    textAlign: 'right !important',
    fontSize: '0.8rem !important',
    marginLeft: `${theme.spacing(1)} !important`,
    opacity: '0.5 !important',
  },
});

const ItemDiscussion = ({ event }: { event: any }) => {
  const theme = useTheme();
  const classes = useItemStyles(theme);
  const { conferenceState } = useContext(ConferenceContext);
  const isDiscussion = event.type === 'discussion';

  const displayText = event.content.title || event.content.text || '';
  const text =
    displayText.length > 30 ? `${displayText.slice(0, 30)}...` : displayText;
  const replyToText = event.content.discussion
    ? event.content.discussion.title
    : '';
  const replyTo =
    replyToText.length > 15 ? `${replyToText.slice(0, 15)}...` : replyToText;

  const fromNow = moment
    .utc(event.content.createdAt)
    .utcOffset(-conferenceState.conferenceDetail.utc_offset)
    .fromNow();

  return (
    <ButtonBase
      sx={classes.root}
      href={`/live/discussion?discussion=${
        isDiscussion ? event.content.id : event.content.discussion.id
      }`}
    >
      <Icon sx={classes.icon}>{isDiscussion ? 'forum' : 'reply'}</Icon>
      <Box sx={classes.content}>
        {!isDiscussion && <Box sx={classes.replyTo}>{replyTo}</Box>}
        <Box sx={classes.text}>{text}</Box>
        <Box sx={classes.user}>
          <UserAvatar user={event.content.visitor} size="small" />
          &nbsp;
          <span>
            {event.content.visitor ? event.content.visitor.first_name : '...'}
          </span>
          <Box component="span" sx={classes.date}>
            {fromNow}
          </Box>
        </Box>
      </Box>
    </ButtonBase>
  );
};

export const Render = ({
  node: {
    id,
    override,
    content: { limit, types },
  },
  conferenceId,
}: IProps) => {
  const theme = useTheme();
  const classes = useContainerStyles(theme, {
    override,
  });
  const { authState } = useContext(AuthContext);
  const [forumEvents, setForumEvents] = useState<any[]>([]);
  useEffect(() => {
    axios
      .get(
        `/api/visitor/conferences/${conferenceId}/listlatestdiscussionsandreplies?limit=${
          limit || 5
        }&types=${types || 'all'}`
      )
      .then((response) => {
        setForumEvents(response.data);
      });
  }, []);

  return (
    <Box component="div" sx={classes.root} {...useNodeProps(id)}>
      {authState.user ? (
        <Box display="flex" flexDirection="column">
          {forumEvents.map((event) => (
            <ItemDiscussion key={event.content.id} event={event} />
          ))}
        </Box>
      ) : (
        'Need to be logged in'
      )}
    </Box>
  );
};
